import { useEffect } from "react";
import "./index.css";
import { natsClient } from "./services/NATSClient";
import { UserInitializer } from "./components/UserInitializer";
import { useAppRefresh } from "./hooks/useAppRefresh";
import { styles } from "./styles";

import HomeView from "./views/HomeView";
import LineView from "./views/LineView";
import MachineView from "./views/MachineView";
import SettingsView from "./views/SettingsView";
import HelpView from "./views/HelpView";
import Header from "./components/Header";
import NotFoundView from "./views/NotFoundView";
import DateRangePicker from "./components/DateRangePicker";
import AuthGuard from "./components/AuthGuard";
import SAPDataProvider from "./providers/SAPDataProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSAPDataStore } from "./store/SAPDataStore";
import CompareMachineView from "./views/CompareMachineView";
import CockpitSessionsView from "./views/CockpitSessionsView";
import { ChatBubble } from "./components/cockpit/ChatBubble";
import { ChatPanel } from "./components/cockpit/ChatPanel";

function AppRoutes({
  setDatePickerOpen,
  isDatePickerOpen,
  dateRange,
  setDateRange,
}: {
  setDatePickerOpen: (open: boolean) => void;
  isDatePickerOpen: boolean;
  dateRange: { start: string; end: string };
  setDateRange: (start: string, end: string) => void;
}) {
  const location = useLocation();
  const state = location.state || {};
  const { machines, lines } = useSAPDataStore();

  // Extract machineId from the URL for machine routes
  const machineIdFromPath = location.pathname.startsWith("/machine/")
    ? location.pathname.split("/machine/")[1]
    : null;

  // Extract lineId from the URL for line routes
  const lineIdFromPath = location.pathname.startsWith("/line/")
    ? location.pathname.split("/line/")[1]
    : null;

  // Get machine data from SAP store if on machine route
  const machineData = machineIdFromPath
    ? (() => {
        const machine = machines.find(
          (machine) =>
            machine.maschinennummer === machineIdFromPath ||
            machine.maschineBK === machineIdFromPath ||
            machine.machineId === machineIdFromPath,
        );

        if (machine) {
          // Find the line name from lineId
          const line = lines.find(
            (line) =>
              line.linieBK === machine.lineId || line.lineId === machine.lineId,
          );
          const lineName = line?.linieName;
          const lineNumber = line?.linieNummer;

          // Map the machine data to the format expected by Header
          return {
            machineId: machine.maschinennummer || machine.maschineBK, // Use maschinennummer as the primary ID
            machineName: machine.machineName || machine.bautyp,
            lineId: machine.lineId,
            lineName: lineName,
            lineNumber: lineNumber,
            machineType: machine.bautyp || machine.machineType,
            factory: machine.factoryName,
          };
        }

        return state; // Fallback to route state if machine not found in store
      })()
    : undefined;

  // Get line data from SAP store if on line route
  const lineData = lineIdFromPath
    ? (() => {
        const line = lines.find(
          (line) =>
            line.lineId === lineIdFromPath || line.linieBK === lineIdFromPath,
        );
        if (line) {
          return {
            lineId: line.lineId || line.linieBK,
            lineNumber: line.linieNummer,
            lineName: line.linieName,
            factory: line.factoryName,
            machineCount: line.maschinen?.length || 0,
            machines: line.maschinen || [],
            // OEM number is on the line (not on machines)
            oemNumbers: line.oemNummer ? [line.oemNummer] : [],
          };
        }
        return state;
      })()
    : undefined;

  // Get machine data for DateRangePicker if on machine view
  const machineDataForPicker =
    machineIdFromPath && machines && Array.isArray(machines)
      ? (() => {
          try {
            const machine = machines.find(
              (machine) =>
                machine &&
                (machine.maschinennummer === machineIdFromPath ||
                  machine.maschineBK === machineIdFromPath ||
                  machine.machineId === machineIdFromPath),
            );

            return machine
              ? {
                  machineId: machine.maschinennummer || machine.maschineBK,
                  machineName: machine.machineName || machine.bautyp,
                  serialno: machine.maschinennummer || machine.maschineBK, // Use as serialno for NATS request
                }
              : undefined;
          } catch (error) {
            console.error("Error finding machine data for picker:", error);
            return undefined;
          }
        })()
      : undefined;

  // For Header navigation, provide a navigate function via context or window for legacy components
  (window as any).reactRouterNavigate = useNavigate();

  return (
    <>
      <Header
        currentView={location.pathname.split("/")[1] || "home"}
        machineData={machineData}
        lineData={lineData}
      />

      {/* Modal Date Range Picker inside Router context */}
      {isDatePickerOpen && (
        <div
          className={styles.app.modal.overlay}
          aria-modal="true"
          role="dialog"
          onClick={() => setDatePickerOpen(false)}
        >
          <div
            className={styles.app.modal.container}
            style={{ pointerEvents: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <DateRangePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              machineData={machineDataForPicker}
              onChange={(start, end, action) => {
                if (action === "apply") {
                  setDateRange(start, end);
                  setDatePickerOpen(false);
                } else if (action === "cancel") {
                  setDatePickerOpen(false);
                }
                // Do not close modal or update range on other actions
              }}
            />
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<HomeView />} />
        <Route path="/line/:lineId" element={<LineView />} />
        <Route path="/machine/:machineId" element={<MachineView />} />
        <Route path="/compare-machine" element={<CompareMachineView />} />
        <Route path="/settings" element={<SettingsView />} />
        <Route path="/cockpit/sessions" element={<CockpitSessionsView />} />
        <Route path="/help" element={<HelpView />} />
        <Route path="*" element={<NotFoundView />} />
      </Routes>

      {/* AI Chatbot: floating action button + chat panel overlay */}
      <ChatBubble />
      <ChatPanel />
    </>
  );
}

function App() {
  const { dateRange, isDatePickerOpen, setDateRange, setDatePickerOpen } =
    useSAPDataStore();

  // Initialize automatic refresh strategies
  useAppRefresh();

  // NATS connection lifecycle is managed by SAPDataProvider
  // Disconnect only on actual page unload, not React unmount (Strict Mode double-fires cleanups)
  useEffect(() => {
    const handleUnload = () => natsClient.disconnect();
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // Lock scroll when modal is open
  useEffect(() => {
    if (isDatePickerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDatePickerOpen]);

  return (
    <ThemeProvider defaultTheme="light">
      <AuthGuard>
        <UserInitializer />
        <div className={styles.app.container}>
          <div aria-hidden={isDatePickerOpen ? "true" : undefined}>
            <BrowserRouter>
              <SAPDataProvider>
                <AppRoutes
                  setDatePickerOpen={setDatePickerOpen}
                  isDatePickerOpen={isDatePickerOpen}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                />
              </SAPDataProvider>
            </BrowserRouter>
          </div>
        </div>
      </AuthGuard>
    </ThemeProvider>
  );
}

export default App;
